import { IndicatorSeries, type Bounding, type IndicatorTemplate } from 'klinecharts';
import { useChartStore } from '@/store/chartStore';
import { drawScanLabels, fmtVolume, fmtPct } from './labelUtils';

type KBarFull = { timestamp: number; open?: number; close?: number; volume?: number; crossVolume?: number | null };

type DrawParams = {
  ctx: CanvasRenderingContext2D;
  kLineDataList: KBarFull[];
  visibleRange: { from: number; to: number };
  xAxis: { convertToPixel: (i: number) => number };
  yAxis: { convertToPixel: (v: number) => number };
  bounding: Bounding;
  indicator: { result: Record<string, never>[] };
};

type VolRow = { vol: number; pct: number; periods: number[]; color: string } | null;

const DEFAULT_VOL_MA = [5, 10, 20];

const volLabels: IndicatorTemplate<Record<string, never>> = {
  name:       'VOL_LABELS',
  shortName:  'VolLabels',
  series:     IndicatorSeries.Normal,
  shouldOhlc: false,
  figures:    [],

  // pass-through calc — data comes from chartStore, not klinecharts figures
  calc: (dataList) => dataList.map(() => ({})),

  draw: (params: unknown): boolean => {
    try {
      const {
        ctx,
        kLineDataList: bars,
        visibleRange,
        xAxis,
        yAxis,
        bounding,
      } = params as DrawParams;

      if (!bars.length) return true;

      const state = useChartStore.getState();
      if (!state.volumeLabelEnabled) return true;

      const scanConfigs = state.indicatorScanConfigs['VOL'] ?? [];
      const enabledRules = scanConfigs.filter((c) => c.enabled && c.ruleId === 'vol_spike');
      if (!enabledRules.length) return true;

      const volParams = state.indicatorParams['VOL']?.length
        ? state.indicatorParams['VOL']
        : DEFAULT_VOL_MA;
      const volColors = state.indicatorColors['VOL'] ?? [];
      const maColors  = volColors.slice(2);
      const hiddenMap = state.volumeLabelHidden ?? {};
      const offsetMap = state.volumeLabelOffsets ?? {};
      const maColorByPeriod = new Map<number, string>();
      volParams.forEach((p, i) => {
        const color = maColors[i];
        if (color) maColorByPeriod.set(p, color);
      });

      const keyForIndex = (i: number) => String(bars[i]?.timestamp ?? i);

      const { crossVolumeEnabled } = state;
      const vols: (number | null)[] = bars.map((b) => {
        const raw = b.volume ?? null;
        if (raw == null) return null;
        if (crossVolumeEnabled) return raw;
        return Math.max(0, raw - (b.crossVolume ?? 0));
      });
      const maByPeriod = new Map<number, (number | null)[]>();

      const getMA = (period: number) => {
        const p = Math.max(1, Math.floor(period));
        const cached = maByPeriod.get(p);
        if (cached) return cached;
        const mas: (number | null)[] = vols.map((_, i) => {
          if (i < p - 1) return null;
          let sum = 0;
          for (let j = i - p + 1; j <= i; j++) {
            const v = vols[j];
            if (v == null) return null;
            sum += v;
          }
          return sum / p;
        });
        maByPeriod.set(p, mas);
        return mas;
      };

      const matchRows: VolRow[] = bars.map((_b, i) => {
        const vol = vols[i];
        if (vol == null || vol === 0) return null;
        let bestPct = -Infinity;
        let bestColor = '#FF9600';
        const periods: number[] = [];

        for (const cfg of enabledRules) {
          const period = cfg.params[0] ?? 20;
          const threshold = cfg.params[1] ?? 0;
          const mas = getMA(period);
          const ma = mas[i];
          if (ma == null || ma === 0) continue;
          const pct = ((vol - ma) / ma) * 100;
          if (pct >= threshold) {
            periods.push(period);
            if (pct > bestPct) {
              bestPct = pct;
              bestColor = maColorByPeriod.get(period) ?? cfg.color;
            }
          }
        }

        if (!periods.length || bestPct === -Infinity) return null;
        periods.sort((a, b) => a - b);
        return { vol, pct: bestPct, periods, color: bestColor };
      });

      drawScanLabels<VolRow>(
        ctx,
        matchRows,
        visibleRange,
        xAxis,
        yAxis,
        bounding,
        {
          condition:   (r, i) => r !== null && !hiddenMap[keyForIndex(i)],
          format:      (r) => [fmtVolume(r!.vol), fmtPct(r!.pct)],
          anchorValue: (r) => r?.vol ?? null,
          color: (r) => r!.color,
          getKey: (_r, i) => keyForIndex(i),
          getOffset: (_r, i) => offsetMap[keyForIndex(i)] ?? { dx: 0, dy: 0 },
          position: 'above',
          leaderLine: { enabled: true, dash: [3, 3], width: 1, alpha: 0.45 },
          closeIcon: { enabled: true, size: 7, padding: 2 },
          topPadding: 18,
          onLayout: (items) => {
            (window as unknown as Record<string, unknown>).__vol_label_layout = items;
          },
        },
      );
    } catch {
      // Swallow draw errors to avoid breaking the chart render loop
    }
    return true;
  },
};

export default volLabels;
